import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { useContent } from "../lib/content";
import { useCurrency } from "../lib/currency";
import { Phone, ChevronRight, LogOut, Wrench, Flame, Store } from "lucide-react";
import { BRANDS, darkLogoFor } from "../lib/brand";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count, total } = useCart();
  const { t } = useContent();
  const { format } = useCurrency();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <header data-testid="navbar">
      {/* Top utility bar */}
      <div className="topbar">
        <div className="max-w-[1200px] mx-auto px-4 h-9 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Phone size={11} />
            <span>{t("topbar_phone_label", "VOLAJTE")}: {t("topbar_phone", "+421 900 000 000")}</span>
          </div>
          <div className="hidden md:block font-bold" style={{ color: "var(--signal)" }}>
            {t("topbar_notice", "CENY V CENNÍKU SÚ NETTO CENY, NEOBSAHUJÚ DPH.")}
          </div>
          <nav className="flex items-center gap-1 text-[11px] uppercase">
            <Link to="/catalog" className="px-2 py-1 hover:text-[color:var(--brand)] flex items-center gap-1" data-testid="nav-catalog">
              Domov
            </Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <Link to="/orders" className="px-2 py-1 hover:text-[color:var(--brand)] flex items-center gap-1" data-testid="nav-orders">
              Moje objednávky
            </Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <Link to="/cart" className="px-2 py-1 hover:text-[color:var(--brand)] flex items-center gap-1" data-testid="cart-button">
              Nákupný košík
            </Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <span className="px-2 py-1 font-bold" data-testid="cart-total-display">
              {format(total)}
              {count > 0 && <span className="ml-1" style={{ color: "var(--signal)" }} data-testid="cart-count-badge">({count})</span>}
            </span>
          </nav>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1200px] mx-auto px-4 py-5 flex items-center justify-between gap-6">
          <Link to="/catalog" className="flex items-center gap-3" data-testid="brand-link">
            <div
              className="w-14 h-14 flex items-center justify-center rounded shrink-0"
              style={{ background: "var(--text)", color: "#fff", fontWeight: 900, fontSize: 24, letterSpacing: "-0.04em" }}
            >
              {t("header_logo_initials", "PS")}
            </div>
            <div className="leading-tight">
              <div className="font-bold text-xl" style={{ color: "var(--text)" }}>{t("header_company_name", "PROPARTS.SK")}</div>
              <div className="text-xs" style={{ color: "var(--text-mute)" }}>{t("header_company_subtitle", "OEM diely · Veľkoobchod")}</div>
            </div>
          </Link>

          <div className="hidden lg:block flex-1 text-center px-4">
            <div className="text-base font-bold uppercase tracking-wide" style={{ color: "var(--signal)" }}>
              {t("header_price_period", "CENY PLATNÉ 04/2026!")}
            </div>
            <div className="text-base font-bold uppercase tracking-wide mt-0.5" style={{ color: "var(--signal)" }}>
              {t("header_price_subtext", "PRE PRESNÉ CENY POŠLITE EMAIL,")}
            </div>
            <div className="text-base font-bold uppercase tracking-wide mt-0.5" style={{ color: "var(--signal)" }}>
              {t("header_price_subtext2", "ALEBO SA PRIHLÁSTE DO ÚČTU.")}
            </div>
          </div>

          <div className="hidden md:block shrink-0" />
        </div>

        {/* Admin row (no search field here — only on /catalog) */}
        <div className="border-t" style={{ borderColor: "var(--border)" }}>
          <div className="max-w-[1200px] mx-auto px-4 py-2 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {user && (
                <span className="text-xs" style={{ color: "var(--text-mute)" }} data-testid="current-user-email">
                  Vitajte, <strong style={{ color: "var(--text)" }}>{user.email}</strong>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {user?.role === "admin" && (
                <NavLink to="/admin" className="btn-ghost !py-1 !px-2 text-[11px]" data-testid="nav-admin">
                  <Wrench size={12} /> Admin
                </NavLink>
              )}
              <button onClick={handleLogout} className="btn-ghost !py-1 !px-2 text-[11px]" data-testid="logout-button">
                <LogOut size={12} /> Odhlásiť
              </button>
            </div>
          </div>
        </div>

        {/* Brand nav (Eurobetom style) */}
        <div className="brand-nav">
          <div className="max-w-[1200px] mx-auto px-4 flex items-center gap-1 overflow-x-auto whitespace-nowrap">
            <Link to="/catalog" className="px-3 py-2.5 text-xs font-bold uppercase hover:bg-white" style={{ color: "var(--brand)" }} data-testid="brand-nav-all">
              Všetky diely
            </Link>
            <Link
              to="/catalog?promo=true"
              className="px-3 py-2.5 text-xs font-bold uppercase hover:bg-white flex items-center gap-1.5"
              style={{ color: "#c8102e" }}
              data-testid="brand-nav-promo"
            >
              <Flame size={13} /> Akcie
            </Link>
            <Link
              to="/catalog?brand=piacter"
              className="px-3 py-2.5 text-xs font-bold uppercase hover:bg-white flex items-center gap-1.5"
              style={{ color: "#0A8043" }}
              data-testid="brand-nav-piacter"
            >
              <Store size={13} /> Akciové trhovisko
            </Link>
            {Object.entries(BRANDS)
              .filter(([key]) => key !== "mercedes")
              .map(([key, b]) => (
              <Link
                key={key}
                to={`/catalog?brand=${key}`}
                className="px-3 py-2.5 text-xs font-bold uppercase hover:bg-white flex items-center gap-1.5"
                style={{ color: "var(--text)" }}
                data-testid={`brand-nav-${key}`}
              >
                <img src={darkLogoFor(b)} alt="" className="h-3.5 w-3.5 object-contain" />
                {b.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
